import type { Terrain } from "@mistsweeper/core";
import type { WorldSource } from "./world-source.ts";

/**
 * A world read through a window of tiles: the terrain and whether each tile is revealed, asked
 * once for every tile of the window and a margin round it, and kept in plain arrays. A scene's
 * build asks the placement rules about every tile of its window, and they ask about the tiles
 * around each again and again (the frontier, shores, forest near meadows, blocks of four); the
 * run answers from a map whose keys are too large for small integers, so every answer cost a
 * lookup and some garbage. Through the window a build of the largest window takes a quarter of
 * the time. Outside the window, and for everything else, the world answers itself.
 */
export class WindowWorld implements WorldSource {
  private readonly world: WorldSource;
  private readonly x0: number;
  private readonly y0: number;
  private readonly span: number;
  private readonly terrains: Uint8Array;
  private readonly revealed: Uint8Array;

  /** `world` read through the square of `span` tiles from (x0, y0). */
  constructor(world: WorldSource, x0: number, y0: number, span: number) {
    this.world = world;
    this.x0 = x0;
    this.y0 = y0;
    this.span = span;
    this.terrains = new Uint8Array(span * span);
    this.revealed = new Uint8Array(span * span);
    for (let j = 0; j < span; j += 1) {
      for (let i = 0; i < span; i += 1) {
        this.terrains[j * span + i] = world.terrain(x0 + i, y0 + j);
        this.revealed[j * span + i] = world.isRevealed(x0 + i, y0 + j) ? 1 : 0;
      }
    }
  }

  /** The index of (x, y) in the arrays, or -1 outside the window. */
  private at(x: number, y: number): number {
    const i = x - this.x0;
    const j = y - this.y0;
    return i >= 0 && j >= 0 && i < this.span && j < this.span ? j * this.span + i : -1;
  }

  isRevealed(x: number, y: number): boolean {
    const k = this.at(x, y);
    return k < 0 ? this.world.isRevealed(x, y) : this.revealed[k] === 1;
  }

  terrain(x: number, y: number): Terrain {
    const k = this.at(x, y);
    return k < 0 ? this.world.terrain(x, y) : (this.terrains[k] as Terrain);
  }

  hasBanner(x: number, y: number): boolean {
    return this.world.hasBanner(x, y);
  }

  adjacent(x: number, y: number): number {
    return this.world.adjacent(x, y);
  }

  isChasm(x: number, y: number): boolean {
    return this.world.isChasm(x, y);
  }

  isClosed(x: number, y: number): boolean {
    return this.world.isClosed(x, y);
  }

  isSettled(x: number, y: number): boolean {
    return this.world.isSettled(x, y);
  }

  get status(): WorldSource["status"] {
    return this.world.status;
  }

  get killer(): WorldSource["killer"] {
    return this.world.killer;
  }

  isFallen(x: number, y: number): boolean {
    return this.world.isFallen?.(x, y) ?? false;
  }
}
